import { Block, type Inline, Mark, type Text } from '@gitbook/slate';
import type { DiffTreeChangeType } from '@gitbook/util-tree-merge';

import {
    BLOCK_DIFF,
    BlockDiffChangeType,
    MARK_DIFF_ADDED,
    MARK_DIFF_DELETED,
    MARK_DIFF_MODIFIED,
} from '../types';
import { transformDeepDiffKey } from './keys';

/*
 * Diffs are represented in the documents in multipel ways.
 */

/** Mapping between diff types and mark types. */
const MARKS: { [key in DiffTreeChangeType<Block>]: string } = {
    added: MARK_DIFF_ADDED,
    deleted: MARK_DIFF_DELETED,
    modified: MARK_DIFF_MODIFIED,
};

/**
 * Annotate a block with a diff by wrapping it in a diff block.
 */
export function annotateByWrappingWithDiff(block: Block, type: BlockDiffChangeType): Block {
    let result = Block.create({
        type: BLOCK_DIFF,
        data: {
            type,
        },
        nodes: [block],
    });

    if (type === BlockDiffChangeType.Deleted) {
        // For deleted blocks, we need to regenerate all keys to avoid conflicts a potential addition of the same nodes
        // somewhere else in the document.
        // (aka: move a block from one place to another)
        result = transformDeepDiffKey(result, type);
    }

    return result;
}
/**
 * Annotate a block with a diff by setting its diff type as data.
 * Some blocks use special formatting for their diffs (like code blocks). We pass them their diff type as data, instead of wrapping them in diff
 * blocks.
 */
export function annotateBySettingDiffData(child: Block, type: DiffTreeChangeType<Block>): Block {
    return child.set(
        'data',
        child.data.merge({
            diff: type,
        })
    );
}

/**
 * Apply a mark of the given diff type to all the Texts contained in a Node.
 */
export function annotateAllTextWithDiff<T extends Block | Inline>(
    node: T,
    diffType: DiffTreeChangeType<T>
): T {
    // @ts-ignore
    return node.mapDescendants((descendant) => {
        if (descendant.object === 'text') {
            return annotateTextWithDiff(descendant, diffType);
        }
        return descendant;
    });
}

/**
 * Apply a mark of the given diff type to a Text
 */
export function annotateTextWithDiff(text: Text, diffType: DiffTreeChangeType<Text>): Text {
    return (
        text
            .addMark(
                0,
                text.text.length,
                Mark.create({
                    type: MARKS[diffType],
                })
            )
            // Make sure this text has a unique key. Otherwise if we have a
            // `removed` text followed by an `added` text, there's a key conflict.
            .set('key', `${diffType}-${text.key}`)
    );
}
